<template>
  <dx-modal
    :value="show"
    @input="close"
    title="Assinar digitalmente"
    size="sm"
    id="digital-signature-modal"
  >
    <div class="btn-group btn-group-block form-group">
      <button class="btn btn-info">Bird ID</button>
      <button class="btn btn-info" disabled>VIDaaS</button>
    </div>

    <div class="loading loading-lg" v-if="loading"></div>
    <template v-else-if="token.isValid">
      <div class="tile tile-centered">
        <div class="tile-icon">
          <div class="avatar avatar-lg avatar-gray">
            <fa-icon :icon="['fal', 'check-circle']" />
          </div>
        </div>
        <div class="tile-content">
          <div class="tile-title">Você possui um token de autorização ativo.</div>
          <div class="tile-subtitle">
            <span class="text-info">Expira {{ expiresIn }}</span>
            <button
              type="button"
              class="btn btn-sm btn-error ml-2"
              @click="revoke"
            >revogar</button>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="form-group" :class="{'has-error': $v.form.username.$error}">
        <label for="digital-signature-username" class="form-label">Usuário (CPF)</label>
        <input
          class="form-input"
          id="digital-signature-username"
          v-model="form.username"
          @change="$v.form.username.$touch()"
          placeholder="Usuário"
        />
        <template v-if="$v.form.username.$error">
          <div
            class="form-input-hint"
            v-if="!$v.form.username.required"
          >Campo obrigatório</div>
        </template>
      </div>

      <div class="form-group" :class="{'has-error': $v.form.password.$error}">
        <label for="digital-signature-password" class="form-label">OTP / Senha</label>
        <input
          class="form-input"
          id="digital-signature-password"
          v-model="form.password"
          @change="$v.form.password.$touch()"
          placeholder="OTP / Senha"
        />
        <template v-if="$v.form.password.$error">
          <div
            class="form-input-hint"
            v-if="!$v.form.password.required"
          >Campo obrigatório</div>
        </template>
      </div>

      <div class="form-group">
        <label for="digital-signature-expiry" class="form-label">Expiração do token</label>
        <select
          id="digital-signature-expiry"
          class="form-select"
          v-model="form.lifetime"
        >
          <option
            v-for="item in lifetimeItems"
            :value="item.value"
            :key="item.value"
          >{{ item.text }}</option>
        </select>
      </div>
    </template>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-icon btn-icon-left btn-primary ml-2"
        :class="{ loading: signing }"
        @click="authenticateAndSign"
        :disabled="signing"
      ><fa-icon :icon="['fal', 'signature']" /> Assinar</button>
      <button
        type="button"
        class="btn ml-2"
        @click="close"
      >Cancelar</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment/moment';
import { required } from 'vuelidate/lib/validators';
import store from '@/store';

const lifetimeItems = [
  {
    value: 60 * 60,
    text: '1 hora',
  },
  {
    value: 60 * 60 * 8,
    text: '8 horas',
  },
  {
    value: 60 * 60 * 24,
    text: '1 dia',
  },
  {
    value: 60 * 60 * 24 * 3,
    text: '3 dias',
  },
  {
    value: 60 * 60 * 24 * 7,
    text: '7 dias',
  },
];

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    sign: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      loading: false,
      signing: false,
      form: this.blankForm(),
      token: {
        isValid: false,
        expiresAt: null,
      },
      lifetimeItems,
    };
  },
  validations: {
    form: {
      username: { required },
      password: { required },
    },
  },
  mounted() {
    this.init();
  },
  computed: {
    user() {
      return store.state.auth.user;
    },
    expiresIn() {
      if (!this.token.expiresAt) return '';
      return moment().to(this.token.expiresAt);
    },
  },
  methods: {
    async init() {
      this.form.username = this.user.identity.value;
      await this.check();
    },
    setTokenData(data) {
      this.token.isValid = data.isValid;
      this.token.expiresAt = data.expiresAt;
    },
    async check() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/digital-signature/access-tokens/status');
        this.setTokenData(data);
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    async authenticateAndSign() {
      this.signing = true;
      try {
        if (!this.token.isValid) {
          const { data } = await this.$http.post('/digital-signature/access-tokens', this.form);
          this.setTokenData(data);
        }
        await this.sign();
      } catch (e) {
        this.$toast.error(e);
      }
      this.signing = false;
    },
    async revoke() {
      try {
        const { data } = await this.$http.delete('/digital-signature/access-tokens');
        this.setTokenData(data);
      } catch (e) {
        this.$toast.error(e);
      }
    },
    close() {
      this.form = this.blankForm();
      this.$emit('close');
    },
    blankForm() {
      return {
        username: '',
        password: '',
        lifetime: lifetimeItems[1].value,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

#digital-signature-modal {
  .tile {
    border: $border-width solid $green-color-300;
    border-radius: $border-radius;
    padding: $layout-spacing;
    background-color: $gray-color-ultra-light;
    .avatar {
      color: $green-color-600;
    }
  }
}
</style>
